import React, { useEffect } from 'react'
import ChatBubbleIcon from '@material-ui/icons/ChatBubbleOutline'
import CloseIcon from '@material-ui/icons/Close'
import FeedbackIcon from '@material-ui/icons/FeedbackOutlined'
import HelpIcon from '@material-ui/icons/HelpOutline'
import AskIcon from '@material-ui/icons/EmojiObjectsOutlined'
import MenuBookIcon from '@material-ui/icons/MenuBookOutlined'
import {
  Badge,
  ClickAwayListener,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Paper,
  Typography
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useUser } from '@paintscout/ui-business'
import type { AnalyticsEvent } from '@paintscout/util/analytics'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { track } from 'shared/util/analytics'
import { isE2E } from 'shared/util/e2e'
import {
  WIDGET_BUTTON_BOTTOM,
  WIDGET_BUTTON_COLOR,
  WIDGET_BUTTON_COLOR_HOVER,
  WIDGET_BUTTON_SHADOW,
  WIDGET_BUTTON_SIZE,
  WIDGET_PANEL_BOTTOM
} from 'shared/util/supportWidgetButton'
import type { ResourceCenterEntry, ResourceCenterEntryId } from './entries'
import { getResourceCenterEntries, runResourceCenterEntry } from './entries'
import {
  closeResourceCenter,
  handOffFromResourceCenter,
  openResourceCenter,
  setResourceCenterActive,
  useResourceCenterState
} from './resourceCenterStore'

// Userflow's resource center sat at 1309: above app content and MUI dialogs' backdrop band, below
// HubSpot chat and GitBook's panel. The announcement modal opened from history closes the panel first.
const Z_INDEX = 1309
const RIGHT_OFFSET = 16

const ENTRY_ICONS: Record<ResourceCenterEntry['id'], React.ComponentType> = {
  help: MenuBookIcon,
  ask: AskIcon,
  feedback: FeedbackIcon,
  chat: ChatBubbleIcon
}

const useStyles = makeStyles((theme: Theme) => ({
  launcher: {
    position: 'fixed',
    right: RIGHT_OFFSET,
    bottom: WIDGET_BUTTON_BOTTOM,
    width: WIDGET_BUTTON_SIZE,
    height: WIDGET_BUTTON_SIZE,
    zIndex: Z_INDEX,
    color: '#fff',
    backgroundColor: WIDGET_BUTTON_COLOR,
    boxShadow: WIDGET_BUTTON_SHADOW,
    '&:hover, &:focus-visible': {
      backgroundColor: WIDGET_BUTTON_COLOR_HOVER
    },
    '@media print': {
      display: 'none'
    }
  },
  panel: {
    position: 'fixed',
    right: RIGHT_OFFSET,
    width: 360,
    maxWidth: `calc(100vw - ${RIGHT_OFFSET * 2}px)`,
    overflowY: 'auto',
    zIndex: Z_INDEX,
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1, 1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  title: {
    fontWeight: 600
  },
  section: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  }
}))

export interface ResourceCenterProps {
  /** Show the round launcher in the corner. Painter-view opens the panel from its account menu instead. */
  launcher?: boolean
  /** Offer Live Chat. Only the contractor app mounts `HubspotChat`. */
  chat?: boolean
  /** Unread count on the launcher (announcements). */
  badge?: number
  /** Onboarding checklist (#25354). Rendered first. */
  checklist?: React.ReactNode
  /** Announcement history (#25346), contractor app only. */
  announcements?: React.ReactNode
  /** `painter_resource_center_opened` in painter-view; see `ResourceCenterEvent`. */
  eventName?: Extract<AnalyticsEvent, 'resource_center_opened' | 'painter_resource_center_opened'>
}

/**
 * True when this user gets the in-house resource center: `resourceCenterNext` on, not a superadmin or a
 * masquerade (they get no Userflow either), and not a BugBug run.
 */
export function useResourceCenterEnabled(): boolean {
  const { resourceCenterNext } = useFlags()
  const { isSuperadmin, isMasquerading } = useUser()
  return !!resourceCenterNext && !isSuperadmin && !isMasquerading && !isE2E()
}

/** Sends the resource center event for one entry. `entry: 'panel'` is the panel itself. */
export function trackResourceCenter(
  eventName: NonNullable<ResourceCenterProps['eventName']>,
  entry: ResourceCenterEntryId | 'panel',
  properties?: Record<string, string>
): void {
  track(eventName, { entry, ...properties })
}

/**
 * The in-house resource center (#25353): a launcher in the corner Userflow's resource center used,
 * and a panel of entries that hand off to the widgets we already own. Renders nothing unless
 * `useResourceCenterEnabled`. While mounted and enabled, `setUserflowVisible` moves this launcher
 * instead of Userflow's (`shared/util/userflowVisibility.ts`).
 *
 * Flag removal: #25355 retires Userflow, then drops `resourceCenterNext`, the header history button
 * and the `userflow.openResourceCenter()` branches.
 */
export default function ResourceCenter({
  launcher = true,
  chat = false,
  badge = 0,
  checklist,
  announcements,
  eventName = 'resource_center_opened'
}: ResourceCenterProps) {
  const classes = useStyles()
  const enabled = useResourceCenterEnabled()
  const { user } = useUser()
  const { open, cornerYielded } = useResourceCenterState()

  useEffect(() => {
    if (!enabled) return
    setResourceCenterActive(true)
    return () => setResourceCenterActive(false)
  }, [enabled])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeResourceCenter()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])

  if (!enabled) return null

  const entries = getResourceCenterEntries({ chat })
  const showLauncher = launcher && !cornerYielded

  const toggle = () => {
    if (open) {
      closeResourceCenter()
      return
    }
    if (openResourceCenter()) {
      trackResourceCenter(eventName, 'panel', { source: 'launcher' })
    }
  }

  const onEntry = (id: ResourceCenterEntry['id']) => {
    // Closes first, so the widget that opens takes the corner from a closed panel, and marks that
    // widget as opened from the panel so closing it returns here.
    if (handOffFromResourceCenter(() => runResourceCenterEntry(id, { email: user?.email }))) {
      trackResourceCenter(eventName, id)
    }
  }

  const panelBottom = launcher ? WIDGET_PANEL_BOTTOM : WIDGET_BUTTON_BOTTOM

  return (
    <>
      {/* Mounted only while open, so the click that opened the panel (the account menu in
          painter-view) is not read as a click away. The launcher sits outside it: its own click
          closes the panel, and a second close is a no-op. */}
      {open && (
        <ClickAwayListener onClickAway={closeResourceCenter}>
          <Paper
            className={classes.panel}
            elevation={8}
            style={{ bottom: panelBottom, maxHeight: `calc(100vh - ${panelBottom + 16}px)` }}
            role="dialog"
            aria-label="Help and resources"
            data-testid="resource-center-panel"
          >
            <div className={classes.header}>
              <Typography variant="subtitle1" className={classes.title}>
                Help & resources
              </Typography>
              <IconButton size="small" aria-label="Close help and resources" onClick={closeResourceCenter}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
            {/* Slot order: checklist (#25354), announcements, then the links. */}
            {checklist && <div className={classes.section}>{checklist}</div>}
            {announcements && <div className={classes.section}>{announcements}</div>}
            <List className={classes.section} dense>
              {entries.map((entry) => {
                const Icon = ENTRY_ICONS[entry.id]
                return (
                  <ListItem
                    key={entry.id}
                    button
                    onClick={() => onEntry(entry.id)}
                    data-testid={`resource-center-entry-${entry.id}`}
                  >
                    <ListItemIcon>
                      <Icon />
                    </ListItemIcon>
                    <ListItemText primary={entry.label} secondary={entry.description} />
                  </ListItem>
                )
              })}
            </List>
          </Paper>
        </ClickAwayListener>
      )}
      {showLauncher && (
        <IconButton
          className={classes.launcher}
          aria-label={
            open ? 'Close help and resources' : badge ? `Help and resources, ${badge} unread` : 'Help and resources'
          }
          aria-expanded={open}
          onClick={toggle}
          data-testid="resource-center-launcher"
        >
          <Badge badgeContent={open ? 0 : badge} color="error" max={9}>
            {open ? <CloseIcon /> : <HelpIcon />}
          </Badge>
        </IconButton>
      )}
    </>
  )
}
